import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X, Calendar, Building2, CheckCircle, Clock, User, Info, Loader2 } from 'lucide-react';
import { Candidate, candidatesApi } from '../../api/candidates';
import { ReferenceWithAnswers } from '../../api/references';
import { referencesApi } from '../../api/references';
import SendEmailButton from './SendEmailButton';
import { EmailButton } from './ResendEmailButton';
import CopyLinkButton from './CopyLinkButton';
import ViewAnswersButton from './ViewAnswersButton';
import AddReferenceButton from './AddReferenceButton';
import ReferenceAnswersModal from './ReferenceAnswersModal';
import ReferenceContactModal from './ReferenceContactModal';
import EvaluateWithAIButton from './EvaluateWithAIButton';
import { useLanguage } from '../../contexts/LanguageContext';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useDataRefresh } from '../../contexts/DataRefreshContext';

interface CandidateReferenceModalProps {
  candidate: Candidate;
  onClose: () => void;
  isJobCompleted?: boolean;
}

const CandidateReferenceModal = ({ candidate, onClose, isJobCompleted = false }: CandidateReferenceModalProps) => {
  const { t } = useLanguage();
  const { refreshReferences, refreshCounters } = useDataRefresh();
  const [searchParams] = useSearchParams();
  const [currentCandidate, setCurrentCandidate] = useState<Candidate>(candidate);
  const [loading, setLoading] = useState<boolean>(false);
  const [resendingEmailMap, setResendingEmailMap] = useState<Record<string, boolean>>({});
  const [viewingAnswersReference, setViewingAnswersReference] = useState<ReferenceWithAnswers | null>(null);
  const [viewingContactReference, setViewingContactReference] = useState<ReferenceWithAnswers | null>(null);
  
  // Close modal when Escape key is pressed
  useEscapeKey(onClose);

  // Fetch latest candidate data when references are updated
  useEffect(() => {
    const fetchCandidateData = async () => {
      setLoading(true);
      try {
        const updatedCandidate = await candidatesApi.getById(candidate.id);
        setCurrentCandidate(updatedCandidate);
      } catch (error) {
        console.error('Failed to fetch updated candidate data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateData();
  }, [candidate.id, refreshCounters.references]);

  // Get references from the candidate object
  const candidateReferences = currentCandidate.references || [];
  
  // Check if we should auto-open a specific reference's answers
  useEffect(() => {
    const referenceId = searchParams.get('reference');
    if (referenceId) {
      // Find the reference in the candidate's references
      const reference = candidateReferences.find(ref => ref.id === referenceId);
      // Only show answers if reference has completed the form (check for truthy value)
      if (reference && reference.filled_form_at) {
        setViewingAnswersReference(reference as ReferenceWithAnswers);
      }
    }
  }, [searchParams, candidateReferences]);

  const handleSendReferenceEmail = async (referenceId: string) => {
    setResendingEmailMap(prev => ({ ...prev, [referenceId]: true }));
    try {
      await referencesApi.resendEmail(referenceId);
      console.log('Email sent successfully for reference:', referenceId);
    } catch (error) {
      console.error('Failed to send email:', error);
    } finally {
      setResendingEmailMap(prev => ({ ...prev, [referenceId]: false }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{currentCandidate.full_name}</h2>
              <p className="text-sm text-gray-500">{currentCandidate.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isJobCompleted && (
              <SendEmailButton candidateId={currentCandidate.id} email={currentCandidate.email} disabled={isJobCompleted} />
            )}
            <div className="flex items-center gap-2">
              {!isJobCompleted && (
                <AddReferenceButton
                  candidateId={currentCandidate.id}
                  onReferenceAdded={() => refreshReferences()}
                  disabled={isJobCompleted || loading}
                />
              )}
              <EvaluateWithAIButton candidateId={currentCandidate.id} candidateName={currentCandidate.full_name} />
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-4 max-w-full">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-medium text-gray-900">
                {t('candidates.referenceModals.candidateReferenceModal.references')}
              </h3>
              {loading && (
                <div className="flex items-center gap-2 text-blue-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('common.loading')}</span>
                </div>
              )}
            </div>
            
            {candidateReferences.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <p className="text-gray-500">
                  {t('candidates.referenceModals.candidateReferenceModal.noReferences')}
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border overflow-hidden max-h-[60vh]" style={{display: "block", width: "100%", overflowY: "auto", overflowX: "auto"}}>
                <div style={{minWidth: "100%"}}>
                  <table className="w-full min-w-[700px]">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gray-50/80 border-b">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: "30%"}}>
                          {t('candidates.referenceModals.candidateReferenceModal.tableHeaders.nameRole')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: "20%"}}>
                          {t('candidates.referenceModals.candidateReferenceModal.tableHeaders.company')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: "30%"}}>
                          {t('candidates.referenceModals.candidateReferenceModal.tableHeaders.status')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: "20%"}}>
                          {t('candidates.referenceModals.candidateReferenceModal.tableHeaders.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                    {candidateReferences.map((reference) => {
                      // Check for truthy value instead of just not null
                      const isCompleted = !!reference.filled_form_at;
                      const statusLabel = isCompleted 
                        ? t('candidates.referenceModals.candidateReferenceModal.status.completed') 
                        : t('candidates.referenceModals.candidateReferenceModal.status.pending');
                      const statusIcon = isCompleted ? CheckCircle : Clock;
                      const statusColor = isCompleted ? 'text-green-600' : 'text-amber-500';
                      
                      let formattedDate;
                      if (isCompleted) {
                        formattedDate = new Date(reference.filled_form_at!).toLocaleDateString();
                      } else if (reference.sent_email_at) {
                        formattedDate = `${t('candidates.referenceModals.candidateReferenceModal.status.emailSent')} ${new Date(reference.sent_email_at).toLocaleDateString()}`;
                      } else {
                        formattedDate = t('candidates.referenceModals.candidateReferenceModal.status.notStarted');
                      }
                      
                      return (
                        <tr 
                          key={reference.id} 
                          className={`hover:bg-gray-50/50 transition-colors ${searchParams.get('reference') === reference.id ? 'bg-blue-50' : ''}`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <button 
                                className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left transition-colors flex items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingContactReference(reference as ReferenceWithAnswers);
                                }}
                              >
                                {reference.full_name}
                                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-blue-500" />
                              </button>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {t(`relationships.${reference.relationship_type}`)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">{reference.company}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              {React.createElement(statusIcon, { 
                                className: `h-4 w-4 ${statusColor}` 
                              })}
                              <span className={`text-sm font-medium ${statusColor}`}>
                                {statusLabel}
                              </span>
                              <span className="text-xs text-gray-500 ml-1.5">
                                {formattedDate}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3 flex-wrap">
                              {isCompleted ? (
                                <ViewAnswersButton 
                                  onClick={() => setViewingAnswersReference(reference as ReferenceWithAnswers)}
                                />
                              ) : (
                                <>
                                  <EmailButton 
                                    onSend={() => handleSendReferenceEmail(reference.id)}
                                    isLoading={resendingEmailMap[reference.id] || false}
                                    disabled={isJobCompleted}
                                    email={reference.email}
                                    label={t('candidates.referenceModals.candidateReferenceModal.actions.resend')}
                                    title={t('candidates.referenceModals.candidateReferenceModal.actions.resendTitle')}
                                  />
                                  <CopyLinkButton
                                    referenceId={reference.id}
                                    disabled={isJobCompleted}
                                  />
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for viewing reference answers */}
      {viewingAnswersReference && currentCandidate.jobId && (
        <ReferenceAnswersModal
          reference={viewingAnswersReference}
          jobId={currentCandidate.jobId}
          isOpen={!!viewingAnswersReference}
          onClose={() => setViewingAnswersReference(null)}
        />
      )}

      {/* Modal for viewing reference contact info */}
      {viewingContactReference && (
        <ReferenceContactModal
          reference={viewingContactReference}
          isOpen={!!viewingContactReference}
          onClose={() => setViewingContactReference(null)}
        />
      )}
    </div>
  );
};

export default CandidateReferenceModal;